import { useEffect, useState } from 'react';

// Detect only mobile phones (not tablets/iPads)
// Uses user agent and screen size for accurate detection
export default function useIsMobileDevice() {
  const getIsMobileDevice = () => {
    if (typeof window === 'undefined') return false;

    // User agent detection for mobile phones
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Mobile phone patterns (excluding tablets)
    const mobilePhonePatterns = [
      /Android.*Mobile/i,           // Android phones
      /iPhone/i,                     // iPhones
      /iPod/i,                       // iPod Touch
      /BlackBerry/i,                 // BlackBerry
      /Opera Mini/i,                 // Opera Mini
      /IEMobile/i,                   // Windows Phone
      /Mobile.*Firefox/i,            // Firefox Mobile
      /Mobile.*Chrome/i,             // Chrome Mobile (when not tablet)
    ];

    // Tablet patterns to exclude
    const tabletPatterns = [
      /iPad/i,
      /Android(?!.*Mobile)/i,        // Android tablets (Android without Mobile)
      /Tablet/i,
      /Kindle/i,
      /Silk/i,
      /PlayBook/i,
    ];

    // Check if it's a tablet first
    const isTablet = tabletPatterns.some(pattern => pattern.test(userAgent));
    if (isTablet) return false;

    // Check if it's a mobile phone
    const isMobilePhone = mobilePhonePatterns.some(pattern => pattern.test(userAgent));
    
    // Additional screen size check as fallback
    const isSmallScreen = window.innerWidth <= 768; // Typical mobile breakpoint
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Return true only if it's detected as mobile phone OR has small screen with touch
    return isMobilePhone || (isSmallScreen && hasTouchScreen && !isTablet);
  };

  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(getIsMobileDevice());

  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(getIsMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    
    // Also listen for orientation change
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return isMobileDevice;
}
